import { ColDef, CellEditingStoppedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useMemo, useState, useRef, useCallback } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags } from "src/utils/grid/gridStyleUtils";
import { useMST4030_R01Store } from "./Store_MST4030_R01";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { defaultMST4030_R01DetailDto, MST4030_R01DetailDto } from "./Dto_MST4030_R01";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { formatDate } from "src/utils/string/stringUtil";
import {
	InsertRowHandler,
	createCalendarCellClickHandler,
	CreateCellClickHandler,
	CustomCellClickAction,
	CreateCellDoubleClickHandler,
	CustomCellDoubleClickAction,
	GridCellConfig,
	createCellCssClassRules,
	createGridDropDownCommitAfter,
} from "src/utils/grid/GridHandlerContent";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { GridCalendar } from "src/components/common/form/GridCalendar";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { showWarning } from "src/utils/toast/toastUtil";
import { CommonCodeColumn, getCommonCodeColumnOptions } from "src/utils/grid/GridColumnContent";
import { useMST4030_R01Modal } from "./hooks/useMST4030_R01Modal";
import { useMST4030_R01CellChange } from "./hooks/MST4030_R01CellChange";
import { ShiftRangeSelectionConfig } from "src/utils/grid/useShiftRangeRowSelection";
import { cellDateComparator } from 'src/utils/grid/gridFunctionUtil';


const DEFAULT_EDITABLE_COLUMNS = ["inspectType", "transportQty", "inspectEmpno", "remark"];

const headerNames: Record<string, string> = {
    rowIndex: "",
    inspectDt: "일자",
    inspectType: "변경내역",
    transportQty: "수량",
    inspectEmpno: "등록자",
    inspectEmpNm: "등록자명",
    remark: "비고",
};
const COLUMN_STATE_KEY = "MST4030_R01-detail-grid-column-state";


interface Props {
	gridRef: React.RefObject<AgGridReact<MST4030_R01DetailDto> | null>;
	defaultMST4030_R01DetailRow: MST4030_R01DetailDto;
}

export const DetailTable_MST4030_R01 = (props: Props) => {
	const { gridRef, defaultMST4030_R01DetailRow } = props;

	const {
		MST4030_R01DetailList,
		selectedMST4030_R01DetailList, deletedMST4030_R01DetailList,
		insertMST4030_R01DetailList, removeMST4030_R01DetailList, updateMST4030_R01DetailList,
		setSelectedMST4030_R01DetailList, addDeletedMST4030_R01DetailList,	initSelectedMST4030_R01DetailList,
		originalMST4030_R01DetailList,
		MST4030_R01GridCalendarState, setMST4030_R01GridCalendarState,
		detailColumnState,
		setDetailColumnState,
		detailScrollTop, setDetailScrollTop,
	} = useMST4030_R01Store();

	const { pickerVisible, pickerPosition, targetRowIndex, targetField, targetDate } =
		MST4030_R01GridCalendarState;

	const { inspectionStatusList } = useCommonCodeStore();

	const [clickable, setClickable] = useState(true);
	const gridWrapperRef = useRef<HTMLDivElement>(null);

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
	} = useGridSettingContent({
		gridRef,
		key: "valueIndex",
		gridData: MST4030_R01DetailList,
		defaultGridData: defaultMST4030_R01DetailRow,
		selectedRow: selectedMST4030_R01DetailList,
		originalGridData: originalMST4030_R01DetailList,
		updateRow: (index: number, data: any) => updateMST4030_R01DetailList(index, data),
	});



	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: ["inspectEmpNm"],
		insertedDisabledCells: ["inspectEmpNm"],
		commonDisabledCells: [],
		pointerCells: ["inspectDt", "inspectType", "transportQty", "inspectEmpno", "remark"],
		insertedPointerCells: ["inspectDt", "inspectType", "transportQty", "inspectEmpno", "remark"],
		commonPointerCells: [],
		alignLeftCells: [],
	}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
		const baseRules = createCellClassRules<MST4030_R01DetailDto>({
			idKey: 'valueIndex',
			selectedList: selectedMST4030_R01DetailList,
			deletedList: deletedMST4030_R01DetailList,
			disabledInsertedColumns, disabledColumns,
			pointerInsertedColumns, pointerColumns,
			alignLeftColumns,
		});
		return { ...defaultColDef, cellClassRules: { ...baseRules, }};
	}, [
		selectedMST4030_R01DetailList, deletedMST4030_R01DetailList,
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
		createCellClassRules,
	]);

	const handleRemoveDetailRow = useCallback(
		(rowData: MST4030_R01DetailDto) => {
			if (MST4030_R01DetailList.length <= 1) {
				showWarning("데이터가 한 개만 있을 때는 삭제가 불가능합니다.");
				return;
			}
			if (rowData.valueIndex === null || rowData.valueIndex === undefined) return;

			setClickable(false);

			if (rowData.inserted) {
				gridRef.current?.api.applyTransaction({ remove: [rowData] });
				removeMST4030_R01DetailList(rowData.valueIndex);
			} else {
				addDeletedMST4030_R01DetailList([rowData.valueIndex.toString()]);
				gridRef.current?.api.refreshCells({ force: true });
			}
		},
		[
			MST4030_R01DetailList.length,
			gridRef,
			addDeletedMST4030_R01DetailList,
			removeMST4030_R01DetailList,
		],
	);

	const isEditable = (params: any) => {
		if (!params.data) return false;
		const valueIndex = params.data.valueIndex?.toString() || "";
		const isDeleted = deletedMST4030_R01DetailList.includes(valueIndex);
		return !isDeleted;
	};

	const handleCellEditingStopped = useCallback(
		(e: CellEditingStoppedEvent<MST4030_R01DetailDto>) => {
			if (e.colDef.field !== "inspectDt") return;

			setTimeout(() => {
				const gridDiv = gridWrapperRef.current;
				if (!gridDiv) return;

				const cellSelector = `[row-index="${e.rowIndex}"] [col-id="inspectDt"]`;
				const cellElement = gridDiv.querySelector<HTMLElement>(cellSelector);

				if (cellElement) {
					const cellRect = cellElement.getBoundingClientRect();
					setMST4030_R01GridCalendarState({
						pickerPosition: {
							top: cellRect.bottom + window.scrollY,
							left: cellRect.left + window.scrollX,
						},
						targetRowIndex: e.rowIndex!,
						targetField: "inspectDt",
						pickerVisible: true,
					});
				}
			}, 0);
		},
		[setMST4030_R01GridCalendarState],
	);

	const cellEventHandlers = useDetailTableEvents({
		detailList: MST4030_R01DetailList,
		editableColumns: DEFAULT_EDITABLE_COLUMNS,
		keyDownConfig: { enableArrowNavigation: true },
	});

	const cellClickActions: CustomCellClickAction<MST4030_R01DetailDto>[] = useMemo(
		() => [
			{
				validate: (field) => DEFAULT_EDITABLE_COLUMNS.includes(field),
				action: (e) => {
					e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! });
				},
			},
			{
				validate: (field) => field === "inspectDt",
				action: createCalendarCellClickHandler(
					"inspectDt",
					MST4030_R01GridCalendarState,
					setMST4030_R01GridCalendarState,
				),
			},
		],
		[MST4030_R01GridCalendarState, setMST4030_R01GridCalendarState],
	);

	const handleCellClick = CreateCellClickHandler(MST4030_R01DetailList, "valueIndex", cellClickActions);

	const { settingCodeSearchModalProps } = useMST4030_R01Modal({
		updateMST4030_R01DetailList,
		gridRef,
	});

	const cellDoubleClickActions: CustomCellDoubleClickAction<MST4030_R01DetailDto>[] = useMemo(
		() => [
			{
				validate: (field) => field === "inspectEmpno",
				action: (_e, foundData) => {
					settingCodeSearchModalProps(
						"등록자",
						foundData.inspectEmpno || "",
						false,
						"inspectEmpno",
						foundData,
						foundData.valueIndex,
					);
				},
			},
		],
		[settingCodeSearchModalProps],
	);

	const handleCellDoubleClicked = CreateCellDoubleClickHandler(
		MST4030_R01DetailList,
		"valueIndex",
		cellDoubleClickActions,
	);

	const handleGridDropDownCommitAfter = useMemo(() => 
		createGridDropDownCommitAfter<MST4030_R01DetailDto>(DEFAULT_EDITABLE_COLUMNS),
	[],);


	const baseColumnDefs: ColDef<MST4030_R01DetailDto>[] = useMemo(
		() => [
			...getDefaultRowIndexColumnConfig((params: MST4030_R01DetailDto) =>
				renderIndexCell(
					params,
					(innerParams: MST4030_R01DetailDto) =>
						getRowStyleFlags(innerParams, {
							deletedList: deletedMST4030_R01DetailList,
							selectedList: selectedMST4030_R01DetailList,
							dataList: MST4030_R01DetailList,
						}),
					(rowIndex: number, e: React.MouseEvent) =>
						InsertRowHandler({
							event: e,
							dataList: MST4030_R01DetailList,
							rowData: MST4030_R01DetailList[rowIndex],
							insertRow: insertMST4030_R01DetailList,
							defaultRowData: defaultMST4030_R01DetailRow,
							setClickable,
						}),
					(rowData) => handleRemoveDetailRow(rowData),
				),
			),
			{
				headerName: "일자", colId: "inspectDt", field: "inspectDt", width: 120,
				editable: isEditable,
				valueFormatter: (params: any) =>
					params.data?.inspectDt ? formatDate(new Date(params.data?.inspectDt)) : "",
				comparator: cellDateComparator,
			},
			{
				headerName: "변경내역", colId: "inspectType", field: "inspectType", width: 120,
				...CommonCodeColumn.options(inspectionStatusList, true, CommonCodeColumn.change(updateMST4030_R01DetailList), handleGridDropDownCommitAfter),
			},
			{
				headerName: "수량", colId: "transportQty", field: "transportQty", width: 100,
				editable: isEditable,
				valueFormatter: (params) => (params.value ? params.value.toLocaleString() : "0"),
				cellStyle: { textAlign: "right" },
				comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
				pasteable: true,
				valueParser: (params) =>
					Number(String(params.newValue ?? "").replaceAll(',', "").trim()) || 0,
			},
			{
				headerName: "등록자", colId: "inspectEmpno", field: "inspectEmpno", width: 120,
				editable: isEditable,
				pasteable: true,
			},
			{
				headerName: "등록자명", colId: "inspectEmpNm", field: "inspectEmpNm", width: 120,
				editable: false,
			},
			{
				headerName: "비고", colId: "remark", field: "remark", width: 200,
				editable: isEditable,
				pasteable: true,
			},
		],
		[
			deletedMST4030_R01DetailList,
			MST4030_R01DetailList,
			selectedMST4030_R01DetailList,
			inspectionStatusList,
			handleRemoveDetailRow,
		],
	);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
	[baseColumnDefs]);

	const columnDefs: ColDef<MST4030_R01DetailDto>[] = useMemo(() => {
		if (detailColumnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<MST4030_R01DetailDto>({
			baseColumnDefs,
			columnState: detailColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [baseColumnDefs, detailColumnState, headerNames]);

	const shiftRangeSelectionConfig: ShiftRangeSelectionConfig<MST4030_R01DetailDto> = useMemo(() => ({
		enableShiftRangeSelect: true,
		idKey: "valueIndex",
		selectedIds: selectedMST4030_R01DetailList as Array<string | number>,
		setSelectedIds: (ids: Array<string | number>) => setSelectedMST4030_R01DetailList(ids.map(String)),
		isRowSelectable: (event) => {
			if (!clickable) {
				setClickable(true);
				return false;
			}
			const valueIndex = event.data?.valueIndex;
			if (valueIndex === undefined || valueIndex === null) return false;
			return !deletedMST4030_R01DetailList.includes(String(valueIndex));
		},
	}), [selectedMST4030_R01DetailList, setSelectedMST4030_R01DetailList, clickable, setClickable, deletedMST4030_R01DetailList]);

	const { handleCellValueChanged } = useMST4030_R01CellChange({
		updateMST4030_R01DetailList,
		settingCodeSearchModalProps,
	});

	const pinnedBottomRow = useMemo(
		() => [
			{
				rowIndex: "",
				inspectDt: `${MST4030_R01DetailList.length.toLocaleString()}건`,
				editable: false,
			},
		],
		[MST4030_R01DetailList],
	);

	return (
		<GridContextWrapper<MST4030_R01DetailDto>
			gridRef={gridRef}
			gridWrapperRef={gridWrapperRef}
			paddedGridData={paddedGridData}
			enableCellCalculation
			initSelected={initSelectedMST4030_R01DetailList}
			defaultRowData={defaultMST4030_R01DetailRow}
			baseColumnDefs={detailColumnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setDetailColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Excel<MST4030_R01DetailDto>
				ref={gridRef}
				rowData={paddedGridData}
				getRowId={getRowId}
				columnDefs={columnDefs}
				defaultColDef={cellCssDefaultColDef}
				rowClassRules={createRowClassRules<MST4030_R01DetailDto>({
					idKey: "valueIndex",
					selectedList: selectedMST4030_R01DetailList,
					deletedList: deletedMST4030_R01DetailList,
				})}
				shiftRangeSelection={shiftRangeSelectionConfig}
				onCellClicked={handleCellClick}
				onCellDoubleClicked={handleCellDoubleClicked}
				onCellKeyDown={cellEventHandlers.handleCellKeyDown}
				onCellValueChanged={handleCellValueChanged}
				onCellEditingStopped={handleCellEditingStopped}
				pinnedBottomRowData={pinnedBottomRow}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setDetailColumnState}
				getDefaultColumnState={() => defaultColumnState}
				scrollTop={detailScrollTop}
				setScrollTop={setDetailScrollTop}
			/>
			<GridCalendar
				rowIndex={targetRowIndex!}
				targetField={targetField!}
				visible={pickerVisible}
				setVisible={(visible) => setMST4030_R01GridCalendarState({ pickerVisible: visible })}
				value={targetDate}
				pickerPosition={pickerPosition}
				setPickerPosition={(position) =>
					setMST4030_R01GridCalendarState({ pickerPosition: position })
				}
				onClear={() => {
					setMST4030_R01GridCalendarState({ targetDate: undefined });
				}}
				onChange={(date, rowIndex, targetField) => {
					if (rowIndex !== null && targetField) {
						const foundData = MST4030_R01DetailList[rowIndex];
						if (foundData) {
							updateMST4030_R01DetailList(foundData.valueIndex, {
								...foundData,
								[targetField]: date ?? new Date(),
								isModified: true,
							});
						}
					}
				}}
				onClickLoad={(rowIndex, targetField) => {
					const foundData = MST4030_R01DetailList[rowIndex];
					const date = foundData[targetField as keyof MST4030_R01DetailDto] as Date;
					setMST4030_R01GridCalendarState({
						targetDate: date ? new Date(date) : new Date(),
					});
				}}
			/>
		</GridContextWrapper>
	);
};
